import { Steps, Progress, Statistic, Space, Typography } from 'antd';
import { TeamOutlined, AppstoreOutlined, PauseCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { flowTimeline, formatCountdown, stepMinutes } from '../../utils/gathering';

const { Text } = Typography;

/** "Common room" / "Round 2" — breakout rounds are numbered in flow order. */
export function stepLabels(steps) {
    let round = 0;
    return steps.map(s => (s.kind === 'breakout' ? `Round ${++round}` : 'Common room'));
}

export const StepKindIcon = ({ kind }) => (kind === 'breakout' ? <AppstoreOutlined /> : <TeamOutlined />);

const clock = ms => new Date(ms).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

/** Times for a step: wall-clock once the flow has started, else minutes from the start. */
export const stepTimes = (startedAt, step) => {
    const at = min => (startedAt != null ? clock(startedAt + min * 60000) : `${min}′`);
    return `${at(step.start)}–${at(step.end)}`;
};

/**
 * The live step's countdown as an antd Statistic — frozen, with a pause
 * icon, while the flow is paused.
 */
export function StepCountdown({ label, remainingMs, paused }) {
    return (
        <Statistic
            title={paused ? `${label} · paused` : `${label} · time left`}
            value={formatCountdown(remainingMs)}
            prefix={paused ? <PauseCircleOutlined /> : <ClockCircleOutlined />}
            styles={{ content: { color: paused ? 'var(--status-warning)' : 'var(--vellby-sky-600)', fontVariantNumeric: 'tabular-nums' } }}
        />
    );
}

/**
 * The flow as antd Steps: one step per flow step, with its length and times.
 * While the flow runs, finished steps are marked done, the live one carries a
 * progress ring, and an overall Progress bar and countdown sit underneath.
 * Time added to a step during the run shows as "+N".
 *
 *   position: flowPosition(...) or null; startedAt: ms or null; paused: bool
 */
function FlowTimeline({ steps, position, startedAt, paused = false }) {
    const timeline = flowTimeline(steps);
    const total = timeline.length ? timeline[timeline.length - 1].end : 0;
    const labels = stepLabels(steps);
    if (!total) return null;

    const liveIndex = position && !position.finished ? position.index : -1;
    const live = liveIndex >= 0 ? timeline[liveIndex] : null;
    const livePercent = live ? Math.round(100 * (1 - position.remainingMs / (stepMinutes(live) * 60000))) : undefined;
    const statusOf = i => {
        if (!position) return 'wait';
        if (position.finished || i < position.index) return 'finish';
        return i === position.index ? 'process' : 'wait';
    };

    return (
        <div className="gathering-timeline">
            <Steps
                size="small"
                responsive
                current={liveIndex >= 0 ? liveIndex : undefined}
                percent={paused ? undefined : livePercent}
                items={timeline.map((s, i) => ({
                    key: s.id ?? s.step_id ?? i,
                    status: statusOf(i),
                    icon: i === liveIndex && paused ? <PauseCircleOutlined /> : undefined,
                    title: (
                        <Space size={4}>
                            <StepKindIcon kind={s.kind} />
                            {labels[i]}
                        </Space>
                    ),
                    content: (
                        <Space orientation="vertical" size={0}>
                            <Text type="secondary" style={{ fontSize: 'var(--fs-sm)' }}>
                                {s.duration_minutes} min
                                {s.extended_minutes ? <Text type="success" style={{ fontSize: 'var(--fs-sm)' }}> +{s.extended_minutes}</Text> : null}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 'var(--fs-xs)' }}>{stepTimes(startedAt, s)}</Text>
                        </Space>
                    ),
                }))}
            />
            {position && (
                <div className="gathering-timeline-status">
                    {live ? (
                        <StepCountdown label={labels[liveIndex]} remainingMs={position.remainingMs} paused={paused} />
                    ) : (
                        <Statistic title="Flow" value="Finished" />
                    )}
                    <div className="gathering-timeline-overall">
                        <Progress
                            percent={Math.min(100, Math.round((100 * position.elapsedMin) / total))}
                            status={position.finished ? 'success' : paused ? 'normal' : 'active'}
                            size="small"
                        />
                        <Text type="secondary" style={{ fontSize: 'var(--fs-xs)' }}>
                            {Math.min(total, Math.floor(position.elapsedMin))} of {total} min
                        </Text>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FlowTimeline;
